/**
 * Review screen for pending Risk Analysis translation requests — accept/reject.
 *
 * This is a deliberate web-only enhancement: RA translation review only existed on
 * mobile before, this brings web to parity (mirrors VGPlan/TranslationRequestsScreen.js,
 * minus the per-file column since an RA has a single translatable document, not a
 * collection of uploaded files).
 *
 * @author Robbe De Geyndt <robbe.degeyndt@aware.be>
 */

'use strict';

//imports
import React from 'react';
import {generatePath, useParams} from 'react-router-dom';
import CustomContainer from '../Components/CustomContainer';
import Loader from '../Components/Loader';
import Project from '../Home/Components/Project';
import {Breadcrumb, Button, Table} from 'react-bootstrap';
import CustomTranslation from '../Components/CustomTranslation';
import ConstructionSiteAction from '../../Actions/ConstructionSiteAction';
import RiskAnalysisAction from '../../Actions/RiskAnalysisAction';
import TranslationRequestAction from '../../Actions/TranslationRequestAction';
import LanguageAction from '../../Actions/LanguageAction';
import G from '../../Actions/GeneralAction';

//define class
class TranslationRequestsScreen extends React.Component {

    //constructor
    constructor(props) {
        super(props);

        //state
        this.state = {
            respondingId: null
        }
    }

    //on mount
    componentDidMount = () => {
        this.updateElements();
    }

    //get data
    updateElements = async () => {

        //get risk analysis
        let ra = await RiskAnalysisAction.get(this.props.params.id);

        //get construction site
        let cs = await ConstructionSiteAction.getConstructionSite(ra.constructionSite.id);

        //fetch requests + allowed languages in parallel
        let [translationRequests, allowedDeepLLanguages] = await Promise.all([
            TranslationRequestAction.getTranslationRequests(ra.id),
            LanguageAction.getAllowedDeepLLanguages()
        ]);

        //set state
        this.setState({
            data: {
                constructionSite: cs,
                riskAnalysis: ra
            },
            translationRequests: translationRequests || [],
            allowedDeepLLanguages: allowedDeepLLanguages || []
        });
    }

    //pending requests only — accepted/rejected requests are no longer actionable here
    getPendingRequests = () => {
        return (this.state.translationRequests || []).filter((r) => r.status === 'pending');
    }

    //accept or reject a request
    respond = async (request, accept) => {
        this.setState({respondingId: request.id});
        await TranslationRequestAction.respondToTranslationRequest(request.id, accept);
        await this.updateElements();
        this.setState({respondingId: null});
    }

    //render view
    render = () => {

        if (this.state.data) {

            let pending = this.getPendingRequests();

            return (
                <CustomContainer>
                    <div>
                        {/* Project header */}
                        <div style={{position: 'sticky', top: '2rem', zIndex: 9}}>
                            <Project
                                float
                                data={this.state.data.constructionSite}
                            />
                        </div>

                        {/* Breadcrumb */}
                        <Breadcrumb className={'vertical-margin-2'}>
                            <Breadcrumb.Item
                                href={generatePath('/')}
                            >
                                <CustomTranslation value={'KEY.Home'} />
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                href={generatePath('/construction_site/:id', {id: this.state.data.constructionSite.id})}
                            >
                                {this.state.data.constructionSite.name}
                            </Breadcrumb.Item>
                            <Breadcrumb.Item
                                href={generatePath('/risk_analysis/:id', {id: this.state.data.riskAnalysis.id})}
                            >
                                <CustomTranslation value={'KEY.Detail Risk Analysis'} />
                            </Breadcrumb.Item>
                            <Breadcrumb.Item active>
                                <CustomTranslation value={'KEY.Review translation requests'} />
                            </Breadcrumb.Item>
                        </Breadcrumb>

                        {/* Title */}
                        <div className={'vertical-margin'}>
                            <h1>
                                <CustomTranslation value={'KEY.Review translation requests'}/>
                            </h1>
                        </div>

                        {pending.length === 0 ?
                            <p><CustomTranslation value={'KEY.No pending translation requests'} /></p>
                            :
                            <div className={'rounded bg-light vertical-padding-2 standard-padding-2'}>
                                <Table striped bordered responsive>
                                    <thead>
                                    <tr>
                                        <th><CustomTranslation value={'KEY.Language'} /></th>
                                        <th><CustomTranslation value={'KEY.Requested by'} /></th>
                                        <th><CustomTranslation value={'KEY.Created at'} /></th>
                                        <th />
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {pending.map((request) => {
                                        const isResponding = this.state.respondingId === request.id;
                                        return (
                                            <tr key={request.id}>
                                                <td>
                                                    {LanguageAction.getLanguageDisplayName(request.language, this.state.allowedDeepLLanguages)}
                                                </td>
                                                <td>
                                                    {request.requestedBy ?
                                                        (
                                                            (request.requestedBy.firstName || request.requestedBy.lastName) ?
                                                                (request.requestedBy.firstName || '') + ' ' + (request.requestedBy.lastName || '')
                                                                :
                                                                request.requestedBy.email
                                                        )
                                                        :
                                                        ''
                                                    }
                                                </td>
                                                <td>
                                                    {G.formatDateTime(request.createdAt)}
                                                </td>
                                                <td style={{display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: '0.5rem'}}>
                                                    <Button
                                                        size={'sm'}
                                                        variant={'success'}
                                                        disabled={isResponding}
                                                        onClick={() => this.respond(request, true)}
                                                    >
                                                        <CustomTranslation value={'KEY.Accept'} />
                                                    </Button>
                                                    <Button
                                                        size={'sm'}
                                                        variant={'danger'}
                                                        disabled={isResponding}
                                                        onClick={() => this.respond(request, false)}
                                                    >
                                                        <CustomTranslation value={'KEY.Reject'} />
                                                    </Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                    </tbody>
                                </Table>
                            </div>
                        }
                    </div>
                </CustomContainer>
            );
        } else {
            return (
                <CustomContainer>
                    <Loader />
                </CustomContainer>
            );
        }
    }
}

//export
export default function (props) {
    const params = useParams();
    return <TranslationRequestsScreen {...props} params={params} />;
}
